/**
 * Art. 15 / 20 data export — request it, then put the file in the person's hands.
 *
 * Until 118-03 Settings called `data-export`, received the ZIP through
 * functions.invoke, measured it with JSON.stringify and threw it away: the
 * "Export Ready" alert was the whole delivery. The privacy policy promises a
 * JSON/ZIP that includes documents.
 *
 * The server now stores the archive privately and returns a 15-minute signed
 * URL served as an attachment (supabase/functions/data-export). This module
 * never holds the archive in JS memory:
 *   web    — an anchor on the signed URL; the browser downloads it.
 *   native — expo-file-system downloads it straight to the cache directory,
 *            the share sheet hands it to Files / Drive / Mail, and the cached
 *            copy is deleted whatever happens (zero custody on the device).
 */

import { Platform } from 'react-native';
import * as Sharing from 'expo-sharing';
import { File, Paths } from 'expo-file-system';
import { supabase } from '@/lib/supabase/client';
import { isBackendEnabled } from '@/lib/config/app-mode';
import { handleError, reportError } from '@/lib/errors';

/** The `data-export` 200 body, camel-cased. */
export interface DataExport {
  url: string;
  bytes: number;
  /** ISO time the signed URL stops working. */
  expiresAt: string;
  filename: string;
  /** Something could not be included; data.json `metadata` says what. */
  partial: boolean;
}

export type DataExportErrorKind =
  | 'rate_limited'
  | 'unavailable'
  | 'step_up_required'
  | 'unavailable_offline'
  | 'invalid_response'
  | 'failed';

/**
 * A refusal or failure from the export request, carrying the message to show.
 * `userMessage` is written for the person; `message` stays technical for
 * reporting.
 */
export class DataExportError extends Error {
  readonly kind: DataExportErrorKind;
  readonly userMessage: string;
  readonly status?: number;

  constructor(kind: DataExportErrorKind, userMessage: string, detail?: string, status?: number) {
    super(`data_export_${kind}${detail ? `: ${detail}` : ''}`);
    this.name = 'DataExportError';
    this.kind = kind;
    this.userMessage = userMessage;
    this.status = status;
  }
}

/** `inverio-export-YYYY-MM-DD.zip` (UTC date, matching the server). */
export function exportFileName(date: Date = new Date()): string {
  return `inverio-export-${date.toISOString().slice(0, 10)}.zip`;
}

const SAFE_FILENAME = /^inverio-export-\d{4}-\d{2}-\d{2}\.zip$/;

/** Validate the server contract. A body without a usable link is not an export. */
export function parseDataExportResponse(data: unknown): DataExport {
  const body = (data ?? {}) as Record<string, unknown>;
  const url = body.url;
  const bytes = body.bytes;
  const expiresAt = body.expires_at;
  if (
    typeof url !== 'string' ||
    !url.startsWith('https://') ||
    typeof bytes !== 'number' ||
    !Number.isFinite(bytes) ||
    bytes <= 0 ||
    typeof expiresAt !== 'string'
  ) {
    throw new DataExportError(
      'invalid_response',
      'Your export could not be prepared. Please try again later.',
      'response is missing url/bytes/expires_at',
    );
  }
  // The name ends up as a file on the device: accept only the shape we expect.
  const filename =
    typeof body.filename === 'string' && SAFE_FILENAME.test(body.filename) ? body.filename : exportFileName();
  return { url, bytes, expiresAt, filename, partial: body.partial === true };
}

/** Can this link still be delivered? Needs more than a minute left. */
export function isStillDeliverable(result: DataExport | null, nowMs: number = Date.now()): boolean {
  if (!result) return false;
  const expires = Date.parse(result.expiresAt);
  return Number.isFinite(expires) && expires - nowMs > 60_000;
}

function hoursLabel(seconds: number): string {
  const hours = Math.max(1, Math.ceil(seconds / 3600));
  return hours === 1 ? '1 hour' : `${hours} hours`;
}

/**
 * functions.invoke returns `data: null` for every non-2xx and puts the unread
 * Response on `error.context` — so the status and body that distinguish "come
 * back tomorrow" from "verify with MFA" are only reachable there.
 */
async function toDataExportError(error: unknown): Promise<DataExportError> {
  const err = error as {
    name?: unknown;
    message?: unknown;
    context?: { status?: unknown; json?: () => Promise<unknown> };
  };
  const detail = typeof err?.message === 'string' ? err.message : String(error);
  if (err?.name !== 'FunctionsHttpError' || typeof err.context?.status !== 'number') {
    return new DataExportError('failed', 'Your export could not be prepared. Please try again.', detail);
  }
  const status = err.context.status;
  let body: Record<string, unknown> = {};
  try {
    body = ((await err.context.json?.()) ?? {}) as Record<string, unknown>;
  } catch (parseErr) {
    // An unreadable body still has a status, which is what decides the message.
    reportError(parseErr, { context: 'requestDataExport.errorBody', level: 'warning' });
    body = {};
  }

  if (status === 429) {
    const retry = typeof body.retry_after_seconds === 'number' ? body.retry_after_seconds : 24 * 3600;
    return new DataExportError(
      'rate_limited',
      `You can export your data once every 24 hours. You can request a new export in about ${hoursLabel(retry)}.`,
      detail,
      status,
    );
  }
  if (status === 503) {
    return new DataExportError(
      'unavailable',
      'Data export is temporarily unavailable. Please try again in a minute.',
      detail,
      status,
    );
  }
  // Branch on the stable `code` every requireAAL2 refusal carries (118-05),
  // never on the message text, which is free to change.
  if (status === 403 && body.code === 'aal2_required') {
    return new DataExportError(
      'step_up_required',
      'For your security, exporting your data needs a fresh two-factor check. Sign out, sign back in with your authenticator code, then try again.',
      detail,
      status,
    );
  }
  return new DataExportError('failed', 'Your export could not be prepared. Please try again.', detail, status);
}

/** Ask the server for an export. Throws a DataExportError on any refusal. */
export async function requestDataExport(): Promise<DataExport> {
  if (!isBackendEnabled()) {
    throw new DataExportError(
      'unavailable_offline',
      'Data export needs a live account. It is not available in offline sample mode.',
    );
  }
  const { data, error } = await supabase.functions.invoke('data-export', { method: 'POST' });
  if (error) throw await toDataExportError(error);
  return parseDataExportResponse(data);
}

export type DeliverOutcome =
  | { status: 'delivered' }
  | { status: 'cancelled' }
  | { status: 'failed'; message: string };

/** Rejections the share sheet uses for "the person closed it". */
const DISMISSAL = /did not share|cancel|dismiss/i;

/** A dismissal, shaped so classifyError() reads it as `cancelled`. */
class ExportDismissedError extends Error {
  constructor(cause: unknown) {
    super(`share sheet dismissed: ${cause instanceof Error ? cause.message : String(cause)}`);
    this.name = 'AbortError';
  }
}

function deliverOnWeb(result: DataExport): void {
  // Not fetched: the browser downloads the signed URL itself. The `download`
  // attribute is ignored cross-origin, so the server's Content-Disposition is
  // what makes this a download; the attribute is for same-origin setups.
  const anchor = document.createElement('a');
  anchor.href = result.url;
  anchor.download = result.filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
  }
}

async function deliverOnNative(result: DataExport): Promise<void> {
  if (!(await Sharing.isAvailableAsync())) {
    throw new Error('Sharing is unavailable on this device.');
  }
  const cached = new File(Paths.cache, result.filename);
  try {
    // `idempotent` overwrites a stale copy left by an interrupted earlier run.
    const file = await File.downloadFileAsync(result.url, cached, { idempotent: true });
    try {
      await Sharing.shareAsync(file.uri, {
        mimeType: 'application/zip',
        UTI: 'public.zip-archive',
        dialogTitle: 'Your Inverio data export',
      });
    } catch (err) {
      if (err instanceof Error && DISMISSAL.test(err.message)) throw new ExportDismissedError(err);
      throw err;
    }
  } finally {
    // Zero custody: the device keeps no copy we put there, on any path.
    if (cached.exists) cached.delete();
  }
}

/**
 * Put the export in the person's hands. Never throws: failures are reported
 * once, here, and returned with the message to show.
 */
export async function deliverExport(result: DataExport): Promise<DeliverOutcome> {
  try {
    if (Platform.OS === 'web') deliverOnWeb(result);
    else await deliverOnNative(result);
    return { status: 'delivered' };
  } catch (err) {
    const { message, isCancelled } = handleError(err, { context: 'deliverExport' });
    if (isCancelled) return { status: 'cancelled' };
    const shown =
      err instanceof Error && /sharing is unavailable/i.test(err.message)
        ? 'Sharing is not available on this device, so the export could not be saved.'
        : message;
    return { status: 'failed', message: shown };
  }
}
